import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { ScanLine, ArrowUpRight, ShieldCheck } from "lucide-react";

export default function VerifyPage() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const nav = useNavigate();

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/verify/search?q=${encodeURIComponent(q.trim())}`);
      setResults(data.results);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ec-bg" data-testid="verify-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 border border-ec-line bg-white px-3 py-1 eyebrow">
            <ScanLine className="w-3.5 h-3.5" /> Public verification
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-5">
            Verify any Professional Passport.
          </h1>
          <p className="text-ec-ink-soft mt-3">
            Paste an EthicCred ID (e.g. <span className="font-mono-plex font-bold">AAYY11YYXX</span>)
            or an email address.
          </p>
        </div>

        <form className="mt-10 flex gap-2" onSubmit={search} data-testid="verify-form">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="AAYY11YYXX or email@example.com"
            className="h-12 rounded-sm text-base border-ec-line focus-visible:ring-ec-ink"
            data-testid="verify-query-input"
          />
          <Button type="submit" disabled={loading} className="h-12 rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="verify-search-button">
            {loading ? "Searching…" : "Verify"} <ArrowUpRight className="w-4 h-4" />
          </Button>
        </form>

        {error && (
          <div className="mt-6 text-sm text-ec-error border border-ec-error/30 bg-ec-error/5 px-3 py-2 rounded-sm">
            {error}
          </div>
        )}

        {results && results.length === 0 && (
          <Card className="mt-8 rounded-sm border-ec-line">
            <CardContent className="py-10 text-center text-ec-ink-soft" data-testid="verify-no-results">
              No matching EthicCred Passport found.
            </CardContent>
          </Card>
        )}

        {results && results.length > 0 && (
          <div className="mt-8 space-y-4" data-testid="verify-results">
            {results.map((r) => (
              <Card
                key={r.ethic_id}
                className="rounded-sm border-ec-line cursor-pointer hover:-translate-y-0.5 transition-transform"
                onClick={() => nav(`/verify/${r.ethic_id}`)}
                data-testid={`verify-result-${r.ethic_id}`}
              >
                <CardContent className="p-5 flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-ec-emerald" />
                      <span className="font-display font-bold text-lg">{r.full_name}</span>
                    </div>
                    <div className="text-sm text-ec-ink-soft mt-1">{r.headline || "—"} · {r.location || "Global"}</div>
                    <div className="font-mono-plex font-bold text-sm mt-2">{r.ethic_id}</div>
                  </div>
                  <ArrowUpRight className="w-5 h-5" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <VerificationPolicyFooter />
      </div>
      <Footer />
    </div>
  );
}

function VerificationPolicyFooter() {
  return (
    <div className="mt-16 border-t border-ec-line pt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-ec-ink-soft" data-testid="verification-policy-footer">
      <span className="eyebrow">Verification governance:</span>
      <a className="hover:text-ec-emerald" href="/legal/terms-of-service">Terms of Service</a>
      <a className="hover:text-ec-emerald" href="/legal/privacy-policy">Privacy Policy</a>
      <a className="hover:text-ec-emerald" href="/legal/consent-verification-policy">Consent & Verification Policy</a>
      <a className="hover:text-ec-emerald" href="/legal/community-guidelines">Community Guidelines</a>
    </div>
  );
}
